import React from 'react';
import { cn } from 'src/tef/components/utils/cn';
import type { ComponentSize } from 'src/tef/components/utils/types';

type SpinnerColor = 'primary' | 'secondary' | 'white' | 'current' | 'success' | 'error';

interface LoadingSpinnerProps {
  variant?: {
    size?: ComponentSize;
  };
  color?: SpinnerColor;
  label?: string;
  className?: string;
  inline?: boolean;
  centered?: boolean;
}

const spinnerSizes: Record<ComponentSize, string> = {
  xs: 'w-3 h-3 border',
  sm: 'w-4 h-4 border',
  md: 'w-6 h-6 border-2',
  lg: 'w-8 h-8 border-2',
  xl: 'w-10 h-10 border-[3px]',
};

const colorClasses: Record<SpinnerColor, string> = {
  primary: 'border-primary border-t-transparent',
  secondary: 'border-slate-400 border-t-transparent',
  white: 'border-white border-t-transparent',
  current: 'border-current border-t-transparent',
  success: 'border-green-500 border-t-transparent',
  error: 'border-red-500 border-t-transparent',
};

export default function LoadingSpinner({
  variant = { size: 'md' },
  color = 'primary',
  label = 'Loading',
  className,
  inline = false,
  centered = false,
}: LoadingSpinnerProps) {
  const size = variant.size || 'md';
  const sizeClass = spinnerSizes[size];
  const colorClass = colorClasses[color];

  const spinner = (
    <span
      className={cn('rounded-full animate-spin', inline ? 'inline-block' : 'block', sizeClass, colorClass, className)}
      role="status"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
    </span>
  );

  if (centered) {
    return <span className="flex items-center justify-center w-full h-full min-h-[40px]">{spinner}</span>;
  }

  return spinner;
}

export { LoadingSpinner };
LoadingSpinner.displayName = 'LoadingSpinner';
